import React from "react";

import Link from "next/link";
import dynamic from "next/dynamic";
import { PortableText } from "@portabletext/react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { GROQCTA } from "@/sanity/lib/definitions";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/ui/dialog";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import { cn } from "@/lib/utils";

const HubSpotForm = dynamic(
  () => import("@/components/global/partials/HubSpotForm"),
);
const ZapierForm = dynamic(
  () => import("@/components/global/partials/ZapierForm"),
);

interface CTAWrapperProps extends React.HTMLAttributes<HTMLElement> {
  cta: GROQCTA | null | undefined;
  children: React.ReactNode;
  className?: string;
}

export const CTAWrapper = React.forwardRef<HTMLElement, CTAWrapperProps>(
  function CTAWrapper({ cta, children, className, ...props }, ref) {
    const { isLink, link, linkData, isAnchor, anchor, isPopup, popup } =
      useCTAWrapper(cta);

    if (isLink && link) {
      const openInNewTab = linkData?.openInNewTab ?? false;
      return (
        <Link
          href={link}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
          className={cn("inline-block", className)}
          target={openInNewTab ? "_blank" : "_self"}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
        >
          {children}
        </Link>
      );
    }

    if (isAnchor && anchor) {
      if (!/^#[\w-]+$/.test(anchor)) {
        return (
          <button
            type="button"
            ref={ref as React.Ref<HTMLButtonElement>}
            {...props}
            onClick={(event) => {
              props.onClick?.(event);
              if (event.defaultPrevented) return;

              let selected: HTMLElement | null;
              try {
                selected = document.querySelector<HTMLElement>(anchor);
              } catch {
                return;
              }
              const target = selected;
              if (!target) return;

              const temporaryFocus =
                target.tabIndex < 0 && !target.hasAttribute("tabindex");
              if (temporaryFocus) {
                target.setAttribute("tabindex", "-1");
                target.addEventListener(
                  "blur",
                  () => target.removeAttribute("tabindex"),
                  { once: true },
                );
              }
              target.scrollIntoView({
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
                  .matches
                  ? "auto"
                  : "smooth",
                block: "start",
              });
              target.focus({ preventScroll: true });
            }}
            className={cn("inline-block", className)}
          >
            {children}
          </button>
        );
      }
      return (
        <a
          href={anchor}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
          className={cn("inline-block", className)}
        >
          {children}
        </a>
      );
    }

    if (isPopup && popup) {
      return (
        <Dialog>
          <DialogTrigger asChild>
            <button
              type="button"
              ref={ref as React.Ref<HTMLButtonElement>}
              {...props}
              className={cn("w-full md:w-auto", className)}
            >
              {children}
            </button>
          </DialogTrigger>
          <DialogContent className="max-h-[80svh] overflow-y-auto lg:max-h-screen top-1/2 -translate-y-1/2 bg-white/20 border border-black/[.15] backdrop-blur-[2rem] p-[.75rem] rounded-[1.25rem]">
            <VisuallyHidden>
              <DialogHeader>
                <DialogTitle>{cta?.title || "Details"}</DialogTitle>
                <DialogDescription></DialogDescription>
              </DialogHeader>
            </VisuallyHidden>

            <div className="w-full px-[2.5rem] py-[3rem] sm:px-[3rem] md:px-[4rem] md:py-[5rem] lg:px-[5rem] flex flex-col items-center border bg-white rounded-[1rem] relative overflow-clip">
              {popup?.chip && <Chip text={popup.chip} />}

              {popup?.content && (
                <div className="richtext prose-headings:text-[4rem] lg:prose-headings:text-[5.5rem] w-[min(50rem,100%)] mb-[3rem]">
                  <PortableText
                    value={popup.content}
                    components={customRichTextComponent({})}
                  />
                </div>
              )}

              {popup?.formType === "hs" && popup?.hsForm && (
                <HubSpotForm formData={popup?.hsForm} />
              )}

              {popup?.formType === "zapier" && popup?.zapierForm && (
                <ZapierForm formData={popup.zapierForm} />
              )}
            </div>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        {...props}
        className={cn("inline-block", className)}
      >
        {children}
      </div>
    );
  },
);
